import React, { useState, useEffect, useMemo } from 'react';
import { Box, Alert, Skeleton, Autocomplete, TextField } from '@mui/material';
import { WalteroCard, WalteroTypography, WalteroButton, WalteroDataTable } from '../waltero';
import { subscriptionService } from '../../services/subscriptionService';
import { fetchDevices } from '../../services/apiService';
import { API_BASE_URL, API_ENDPOINTS } from '../../config/api';
import axios from 'axios';
import AssignSensorModal from './AssignSensorModal';
import EditSensorAssignmentModal from './EditSensorAssignmentModal';
import BulkReassignSensorsModal from './BulkReassignSensorsModal';

const SensorAssignments = () => {
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(true);
  const [error, setError] = useState(null);
  const [sensorAssignments, setSensorAssignments] = useState([]);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [subscriptionTypes, setSubscriptionTypes] = useState([]);
  const [organizationSubscriptions, setOrganizationSubscriptions] = useState([]);
  const [selectedOrganizationId, setSelectedOrganizationId] = useState('');
  const [selectedOrganizationOption, setSelectedOrganizationOption] = useState(null);
  const [organizationOptions, setOrganizationOptions] = useState([]);
  const [organizationSearchLoading, setOrganizationSearchLoading] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);

  useEffect(() => {
    const initialize = async () => {
      try {
        setError(null);
        const typesResult = await subscriptionService.getSubscriptionTypes();
        setSubscriptionTypes(typesResult.data || []);
      } catch (err) {
        console.error('Error loading subscription types:', err);
        setError(err.message || 'Failed to load subscription types');
      } finally {
        setInitializing(false);
      }
    };

    initialize();
  }, []);

  useEffect(() => {
    if (!selectedOrganizationId) {
      setSensorAssignments([]);
      setOrganizationSubscriptions([]);
      return;
    }

    loadAssignmentsForOrganization(selectedOrganizationId);
  }, [selectedOrganizationId]);

  const loadAssignmentsForOrganization = async (organizationId) => {
    try {
      setLoading(true);
      setError(null);

      const [assignmentsResult, organizationSubscriptionsResult, devicesResult] = await Promise.all([
        subscriptionService.getSensorSubscriptionsByOrganization(organizationId),
        subscriptionService.getOrganizationSubscriptions(organizationId),
        fetchDevices(organizationId)
      ]);

      // Get ALL sensor subscriptions, not just manual ones
      const allAssignments = assignmentsResult.data || [];

      const orgSubscriptionsData = organizationSubscriptionsResult.data || [];
      const devices = devicesResult?.data || [];

      setOrganizationSubscriptions(orgSubscriptionsData);

      const subscriptionLookup = new Map(
        orgSubscriptionsData.map((subscription) => [subscription.subscriptionTypeId, subscription])
      );
      const defaultSubscription = orgSubscriptionsData.find((subscription) => subscription.isDefault);

      // Create a map of ALL assignments by sensor ID
      const assignmentsBySensor = new Map(
        allAssignments.map((assignment) => [assignment.sensorId, assignment])
      );

      const processedSensors = new Set(devices.map((device) => device.id));

      const combined = devices.map((device) => {
        const sensorId = device.id;
        const sensorName = device.name || device.serialnumber || device.deviceName || sensorId;
        const assignment = assignmentsBySensor.get(sensorId);

        if (assignment) {
          // We have an assignment record (either manual or default)
          const linkedSubscription = subscriptionLookup.get(assignment.subscriptionTypeId);
          const displayName = linkedSubscription?.customName || linkedSubscription?.customDisplayName || getSubscriptionTypeName(assignment.subscriptionTypeId) || assignment.subscriptionTypeId;
          const isManual = assignment.assignmentType === 'manual';

          return {
            ...assignment,
            id: assignment.id || `${assignment.assignmentType}-${sensorId}`,
            sensorId,
            sensorName,
            organizationId,
            assignmentType: assignment.assignmentType || 'manual',
            hasManualAssignment: isManual,
            subscriptionDisplayName: displayName,
            // Preserve ALL activation tracking fields
            activationDate: assignment.activationDate,
            lastActivityDate: assignment.lastActivityDate,
            assignedAt: assignment.assignedAt || assignment.updatedAt || null
          };
        }

        // No assignment record but has default subscription
        if (defaultSubscription) {
          const displayName = defaultSubscription.customName || defaultSubscription.customDisplayName || getSubscriptionTypeName(defaultSubscription.subscriptionTypeId) || defaultSubscription.subscriptionTypeId;

          return {
            id: `default-${sensorId}`,
            sensorId,
            sensorName,
            organizationId,
            subscriptionTypeId: defaultSubscription.subscriptionTypeId,
            assignmentType: 'default',
            hasManualAssignment: false,
            subscriptionDisplayName: displayName,
            assignedAt: defaultSubscription.assignedAt || defaultSubscription.updatedAt || null,
            // No activation data since no record exists
            activationDate: null,
            lastActivityDate: null
          };
        }

        return {
          id: `unassigned-${sensorId}`,
          sensorId,
          sensorName,
          organizationId,
          subscriptionTypeId: null,
          assignmentType: 'unassigned',
          hasManualAssignment: false,
          subscriptionDisplayName: 'No subscription',
          assignedAt: null
        };
      });
      // Process assignments for sensors not in the device list
      const extraAssignments = allAssignments
        .filter((assignment) => !processedSensors.has(assignment.sensorId))
        .map((assignment) => {
          const linkedSubscription = subscriptionLookup.get(assignment.subscriptionTypeId);
          const displayName = linkedSubscription?.customName || linkedSubscription?.customDisplayName || getSubscriptionTypeName(assignment.subscriptionTypeId) || assignment.subscriptionTypeId;
          const isManual = assignment.assignmentType === 'manual';

          return {
            ...assignment,
            id: assignment.id || `${assignment.assignmentType}-${assignment.sensorId}`,
            sensorId: assignment.sensorId,
            sensorName: assignment.sensorId,
            organizationId,
            assignmentType: assignment.assignmentType || 'manual',
            hasManualAssignment: isManual,
            subscriptionDisplayName: displayName,
            // Preserve ALL activation tracking fields
            activationDate: assignment.activationDate,
            lastActivityDate: assignment.lastActivityDate
          };
        });

      const normalizeName = (value, fallback) => {
        if (value === null || value === undefined) {
          return String(fallback ?? '');
        }
        if (typeof value === 'string') {
          return value;
        }
        return String(value);
      };

      const rows = [...combined, ...extraAssignments].sort((a, b) => {
        const nameA = normalizeName(a.sensorName, a.sensorId).toLowerCase();
        const nameB = normalizeName(b.sensorName, b.sensorId).toLowerCase();
        if (nameA < nameB) return -1;
        if (nameA > nameB) return 1;
        return 0;
      });

      setSensorAssignments(rows);
    } catch (err) {
      console.error('Error loading organization sensor assignments:', err);
      setError(err.message || 'Failed to load sensor assignments');
    } finally {
      setLoading(false);
    }
  };

  const searchOrganizations = async (searchTerm = '') => {
    try {
      setOrganizationSearchLoading(true);
      const token = localStorage.getItem('authToken');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      let response;
      if (searchTerm && searchTerm.length >= 2) {
        response = await axios.get(
          `${API_BASE_URL}${API_ENDPOINTS.ORGANIZATIONS.SEARCH}?q=${encodeURIComponent(searchTerm)}`,
          { headers }
        );
      } else {
        response = await axios.get(
          `${API_BASE_URL}${API_ENDPOINTS.ORGANIZATIONS.LIST}?limit=1000`,
          { headers }
        );
      }

      const organizations = response.data.data || response.data || [];
      setOrganizationOptions(organizations);
    } catch (err) {
      console.error('Error searching organizations for sensor assignments:', err);
      if (!error) {
        setError('Failed to load organizations');
      }
    } finally {
      setOrganizationSearchLoading(false);
    }
  };

  const handleOrganizationSelection = (newValue) => {
    setSelectedAssignment(null);
    setError(null);
    if (newValue) {
      setSelectedOrganizationOption(newValue);
      setSelectedOrganizationId(newValue.id);
    } else {
      setSelectedOrganizationOption(null);
      setSelectedOrganizationId('');
    }
  };

  // Helper function to get organization name with caching
  const organizationName = useMemo(() => {
    if (!selectedOrganizationOption) {
      return '';
    }
    return (
      selectedOrganizationOption.name ||
      selectedOrganizationOption.organization_name ||
      selectedOrganizationOption.organizationName ||
      selectedOrganizationOption.id
    );
  }, [selectedOrganizationOption]);

  const hasSensors = sensorAssignments.length > 0;
  const activeOrganizationSubscriptions = useMemo(
    () =>
      organizationSubscriptions.filter(
        (subscription) =>
          subscription.status !== 'inactive' &&
          subscription.subscriptionTypeId
      ),
    [organizationSubscriptions]
  );

  const handleBulkReassignConfirm = async (targetSubscriptionTypeId) => {
    setBulkModalOpen(false);

    if (!selectedOrganizationId || !targetSubscriptionTypeId) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const assignmentsToUpdate = sensorAssignments.filter(
        (assignment) => assignment.subscriptionTypeId !== targetSubscriptionTypeId
      );

      if (assignmentsToUpdate.length === 0) {
        setLoading(false);
        return;
      }

      await Promise.all(
        assignmentsToUpdate.map((assignment) =>
          subscriptionService.assignSensorSubscription(
            assignment.sensorId,
            selectedOrganizationId,
            targetSubscriptionTypeId
          )
        )
      );

      await loadAssignmentsForOrganization(selectedOrganizationId);
    } catch (err) {
      console.error('Error bulk reassigning sensors:', err);
      setError(err.message || 'Failed to reassign sensors');
    } finally {
      setLoading(false);
    }
  };

  const handleAssignmentUpdated = () => {
    if (selectedOrganizationId) {
      loadAssignmentsForOrganization(selectedOrganizationId);
    }
  };

  const getSubscriptionTypeName = (subscriptionType) => {
    if (!subscriptionType) {
      return 'N/A';
    }

    // Handle subscription objects returned directly from the API
    if (typeof subscriptionType === 'object') {
      const possibleName =
        subscriptionType.name ||
        subscriptionType.label ||
        subscriptionType.title;

      if (typeof possibleName === 'string') {
        return possibleName;
      }

      // Handle nested Dynamo-style objects or embedded references
      if (subscriptionType.S) {
        return getSubscriptionTypeName(subscriptionType.S);
      }
      if (subscriptionType.id) {
        return getSubscriptionTypeName(subscriptionType.id);
      }
      if (subscriptionType.subscriptionTypeId) {
        return getSubscriptionTypeName(subscriptionType.subscriptionTypeId);
      }
      if (subscriptionType.subscriptionType) {
        return getSubscriptionTypeName(subscriptionType.subscriptionType);
      }
    }

    const type = subscriptionTypes.find(t => t.id === subscriptionType);
    if (type) {
      return type.name;
    }

    // Fall back to displaying the identifier as text
    return typeof subscriptionType === 'string' || typeof subscriptionType === 'number'
      ? subscriptionType
      : 'N/A';
  };

  const columns = [
    {
      id: 'sensorName',
      label: 'Sensor',
      render: (value, row) => value || row.sensorId || 'Unknown'
    },
    {
      id: 'sensorId',
      label: 'Sensor ID',
      render: (value) => value || 'N/A'
    },
    {
      id: 'subscriptionTypeId',
      label: 'Subscription',
      render: (value, row) => {
        if (row.subscriptionDisplayName) {
          return row.subscriptionDisplayName;
        }
        const source = value || row.subscriptionType || row.subscription || row.subscriptionDetails;
        return getSubscriptionTypeName(source) || 'N/A';
      }
    },
    {
      id: 'assignmentType',
      label: 'Assignment Type',
      align: 'center',
      render: (value) => {
        const backgroundColor = value === 'manual' ? 'info.light' : value === 'default' ? 'success.light' : 'warning.light';
        const textColor = value === 'manual' ? 'info.dark' : value === 'default' ? 'success.dark' : 'warning.dark';
        return (
          <Box
            sx={{
              px: 1,
              py: 0.5,
              borderRadius: 1,
              backgroundColor,
              color: textColor,
              textAlign: 'center',
              fontSize: '0.75rem',
              textTransform: 'capitalize'
            }}
          >
            {value}
          </Box>
        );
      }
    },
    {
      id: 'assignedAt',
      label: 'Assigned Date',
      render: (value, row) => {
        if (row.assignmentType === 'default') {
          return row.assignedAt ? new Date(row.assignedAt).toLocaleDateString() : 'Default';
        }
        if (row.assignmentType === 'unassigned') {
          return 'N/A';
        }
        return value ? new Date(value).toLocaleDateString() : 'N/A';
      }
    },
    {
      id: 'activationDate',
      label: 'Activation Date',
      render: (value, row) => {
        // Show activation date for any sensor that has connected
        if (!value && !row.activationDate) {
          return 'Not activated';
        }
        const dateValue = value || row.activationDate;
        return dateValue ? new Date(dateValue).toLocaleDateString() : 'Not activated';
      }
    },
    {
      id: 'lastActivityDate',
      label: 'Last Activity',
      render: (value, row) => {
        // Show last activity for any sensor that has connected
        if (!value && !row.lastActivityDate) {
          return '-';
        }
        const dateValue = value || row.lastActivityDate;
        return dateValue ? new Date(dateValue).toLocaleDateString() : '-';
      }
    },
  ];

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <strong>Error Loading Sensor Assignments</strong>
          <br />
          {error}
          <Box sx={{ mt: 2 }}>
            <WalteroButton 
              variant="outlined" 
              onClick={() => {
                if (selectedOrganizationId) {
                  loadAssignmentsForOrganization(selectedOrganizationId);
                } else {
                  setError(null);
                  searchOrganizations('');
                }
              }}
              size="small"
            >
              RETRY
            </WalteroButton>
          </Box>
        </Alert>
      )}

      <WalteroCard>
        <Box sx={{ p: 3 }}>
          <Box sx={{ mb: 3 }}>
            <WalteroTypography 
              variant="h5" 
              sx={{ 
                color: 'waltero.primary.coreBlue',
                fontWeight: 600,
                mb: 1
              }}
            >
              Sensor Subscription Assignments
            </WalteroTypography>
          <WalteroTypography 
            variant="body2" 
            sx={{ color: 'text.secondary' }}
          >
            Select an organization to review sensor subscriptions—manual overrides are editable, defaults are shown for unassigned sensors.
          </WalteroTypography>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 2,
              alignItems: 'center',
              mb: 3
            }}
          >
            <Autocomplete
              sx={{ minWidth: 260, flexGrow: 1, maxWidth: 420 }}
              value={selectedOrganizationOption}
              onChange={(event, newValue) => {
                handleOrganizationSelection(newValue);
              }}
              onOpen={() => {
                if (organizationOptions.length === 0) {
                  searchOrganizations('');
                }
              }}
              onInputChange={(event, newInputValue) => {
                if (newInputValue.length >= 2) {
                  searchOrganizations(newInputValue);
                } else if (newInputValue.length === 0) {
                  searchOrganizations('');
                }
              }}
              options={organizationOptions}
              getOptionLabel={(option) => (
                option.name || option.organization_name || option.organizationName || option.id || ''
              )}
              loading={organizationSearchLoading}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Organization"
                  placeholder="Search organization..."
                  helperText="Choose an organization to load manual sensor assignments"
                />
              )}
            />

            <WalteroButton 
              variant="contained" 
              onClick={() => setAssignModalOpen(true)}
              disabled={!selectedOrganizationId || loading}
            >
              ASSIGN SENSOR
            </WalteroButton>

            <WalteroButton
              variant="outlined"
              onClick={() => setBulkModalOpen(true)}
              disabled={!selectedOrganizationId || !hasSensors || activeOrganizationSubscriptions.length <= 1 || loading}
            >
              REASSIGN ALL
            </WalteroButton>
          </Box>

          {selectedOrganizationId ? (
            loading ? (
              <Box>
                {[1, 2, 3, 4, 5].map(i => (
                  <Skeleton key={i} height={60} sx={{ mb: 1 }} />
                ))}
              </Box>
            ) : hasSensors ? (
              <WalteroDataTable
                data={sensorAssignments}
                columns={columns}
                loading={loading}
                title=""
                showTitle={false}
                filterable={false}
                pagination={false}
                showControls={false}
                onRowClick={(event, row) => {
                  if (row?.sensorId) {
                    setSelectedAssignment(row);
                    setEditModalOpen(true);
                  }
                }}
              />
            ) : (
              <Alert severity="info">
                {activeOrganizationSubscriptions.length === 0
                  ? 'Assign a subscription to this organization to enable default sensor assignments.'
                  : `No sensors found for ${organizationName || 'this organization'}.`}
              </Alert>
            )
          ) : initializing ? (
            <Box>
              {[1, 2, 3].map(i => (
                <Skeleton key={i} height={40} sx={{ mb: 1 }} />
              ))}
            </Box>
          ) : (
            <Alert severity="info">Select an organization to view sensor assignments.</Alert>
          )}
        </Box>
      </WalteroCard>

      <AssignSensorModal
        open={assignModalOpen}
        onClose={() => setAssignModalOpen(false)}
        onSuccess={handleAssignmentUpdated}
        defaultOrganizationId={selectedOrganizationId}
        defaultOrganization={selectedOrganizationOption}
      />

      <EditSensorAssignmentModal
        open={editModalOpen}
        assignment={selectedAssignment}
        organizationSubscriptions={activeOrganizationSubscriptions}
        subscriptionTypes={subscriptionTypes}
        onClose={() => {
          setEditModalOpen(false);
          setSelectedAssignment(null);
        }}
        onUpdated={handleAssignmentUpdated}
      />

      <BulkReassignSensorsModal
        open={bulkModalOpen}
        onClose={() => setBulkModalOpen(false)}
        organizationId={selectedOrganizationId}
        organizationName={organizationName}
        organizationSubscriptions={activeOrganizationSubscriptions}
        sensorAssignments={sensorAssignments}
        onConfirm={handleBulkReassignConfirm}
      />
    </Box>
  );
};

export default SensorAssignments;
